import { ComponentProps, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import Missions from '../ChapterView'
import { Box } from '../../../shared_components/UI/Box'
import { theme } from '../../../theme'
import { H2, P } from '../../../shared_components/Library/Typeography'

export default function DropAbout({
  drop,
}: {
  drop: ComponentProps<typeof Missions>['drop']
}) {
  const { rewardsSettings } = drop?.dropSettings ?? {}

  return (
    <Box dc g={60}>
      <Box dc>
        <DistributionChart drop={drop} />
      </Box>
      <Box dc g={100}>
        {rewardsSettings?.map((reward, index) => {
          return (
            <Box dc g={40} key={index}>
              {reward?.distributionDisclaimer && (
                <Box dc g={12}>
                  <H2 s="XL" fw="semibold">
                    {reward.name} Distribution
                  </H2>
                  <P s="L" c="text-secondary">
                    {reward.distributionDisclaimer}
                  </P>
                </Box>
              )}
              {reward?.vestingDisclaimer && (
                <Box dc g={12}>
                  <H2 s="XL" fw="semibold">
                    {reward.name} Vesting
                  </H2>
                  <P s="L" c="text-secondary">
                    {reward.vestingDisclaimer}
                  </P>
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function DistributionChart({
  drop,
}: {
  drop: ComponentProps<typeof Missions>['drop']
}) {
  const [data, setData] = useState<
    { name: String; level: number; Allocation: number }[]
  >([])
  const { n, m, minLevel } = drop?.dropSettings?.distribution ?? {}

  useEffect(() => {
    let calcData = [] as typeof data
    for (let level = 1; level <= 30; level++) {
      const allocationProportion = calculateDropDistributionAllocation({
        level,
        nValue: n,
        mValue: m,
        minLevel,
      })
      calcData = [
        ...calcData,
        {
          name: `Lvl ${level}`,
          level,
          Allocation: allocationProportion,
        },
      ]
    }
    setData(calcData)
  }, [m, minLevel, n])

  if (!n || !m || !minLevel) return null

  return (
    <>
      <Box dc g={12}>
        <H2 s="XL" fw="semibold">
          How distribution works
        </H2>
        <P s="L" c="text-secondary">
          Rewards are distributed according to the chart below, where the x-axis
          represents a users level and the y-axis the corresponding airdrop
          allocation multiplier.
          <br />
        </P>
        <P s="L" c="text-secondary">
          Please note there may be a minimum level before becoming eligible for
          an airdrop.
        </P>
      </Box>
      <ChartBox dc g={40}>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <AreaChart
              width={730}
              height={250}
              data={data}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id="colorAllocation"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={theme.colors.brandPrimary}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={theme.colors.brandPrimary}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" />
              <YAxis />
              <CartesianGrid strokeDasharray="100 0" />
              <Tooltip
                formatter={(value) =>
                  value.toLocaleString('en-GB', { maximumFractionDigits: 1 })
                }
              />
              <Area
                type="monotone"
                dataKey="Allocation"
                stroke={theme.colors.brandPrimary}
                fillOpacity={1}
                fill="url(#colorAllocation)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </ChartBox>
    </>
  )
}

function calculateDropDistributionAllocation({
  level,
  nValue,
  mValue,
  minLevel,
}: {
  level: number
  nValue: number
  mValue: number
  minLevel: number
}) {
  // =IF(level < minLevel, 0, IF(nValue = 1, mValue * (level - minLevel + 1), mValue * nValue^(nValue * (level - minLevel))))
  if (level < minLevel) {
    return 0
  } else if (nValue === 1) {
    return mValue * (level - minLevel + 1)
  } else {
    return mValue * Math.pow(nValue, nValue * (level - minLevel))
  }
}

export const TextTitle = styled.h3`
  color: #fff;
  font-variant-numeric: ordinal;
  font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
  font-family: 'Space Grotesk';
  font-size: 48px;
  font-style: normal;
  font-weight: 700;
  line-height: 54px; /* 112.5% */
  letter-spacing: -0.96px;
  margin-bottom: 40px;
`

export const Text = styled.p`
  color: #fff;
  font-variant-numeric: ordinal;
  font-feature-settings: 'dlig' on, 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px; /* 150% */
  letter-spacing: -0.8px;
  margin: 0;

  ul,
  li {
    all: revert;
  }
`

const ChartBox = styled(Box)`
  border-radius: 8px;
  margin-top: 34px;

  .recharts-default-tooltip {
    background: black !important;
    border: 1px solid rgba(204, 204, 204, 0.2) !important;
  }

  .recharts-cartesian-axis-tick-line {
    display: none;
  }

  .recharts-cartesian-axis-line {
    display: none;
  }

  .recharts-text.recharts-cartesian-axis-tick-value {
    fill: #fff;
    font-family: 'Space Grotesk';
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px; /* 100% */
    letter-spacing: -0.12px;
  }

  .recharts-cartesian-grid {
    opacity: 0.1;
  }

  .recharts-cartesian-grid-horizontal,
  .recharts-cartesian-grid-vertical {
    > line:first-child {
      display: none;
    }
  }

  .recharts-layer.recharts-cartesian-axis.recharts-yAxis.yAxis {
    transform: translateX(-20px);
  }

  .recharts-layer.recharts-cartesian-axis.recharts-xAxis.xAxis {
    transform: translate(5px, 10px);
  }
`
